import {
  useBatchProgress,
  useBatchTracking,
  useDispenseRecords,
  useSampleReports,
} from '@ctcells/api'
import type { MonitoringTraysView } from '@ctcells/api'
import type { Sample } from '@ctcells/contract'
import { errorMessage, t } from '@ctcells/i18n'
import { useStreamState } from '@ctcells/realtime'

import { useActiveBatchSource } from '../../shell/activeBatchSource.ts'
import { useStripBatch } from '../../shell/stripPick.ts'
import { sampleGroupFacts } from '../recipes/recipeForm.ts'
import { labwareOf, reagentsOf } from '../recipes/recipeV2View.ts'

import { chamberProgress, currentDispensePass } from './chamberProgress.ts'
import { chamberPhase } from './flowVocabulary.ts'
import { isTerminalExecutionEvent } from './followRunningDevice.ts'
import type { MonitoringDeviceKey } from './monitoringModel.ts'
import type { SampleFlowView } from './MonitoringScreen.tsx'
import { patientChambers, speakingCell, type GroupAssignment } from './patientChambers.ts'
import {
  batchFlowCells,
  flowRows,
  flowSamples,
  samplesOfBatch,
  stageTallies,
  isFlowStale,
} from './sampleFlowModel.ts'
import { stageTimeLookup } from './stageTimes.ts'
import {
  assignmentSampleGroups,
  groupedSampleIds,
  serverSampleGroups,
} from './serverSampleGroups.ts'
import { stageVerdictLookup } from './stageVerdicts.ts'
import { temporarySampleGroups } from './temporarySampleGroups.ts'
import { trackingChamberProgress } from './trackingProgress.ts'

/**
 * 추적을 못 받았을 때만 쓰는 분주 기록 수(`chamberProgress` 의 웰 근거). 한 바퀴(웰 수)만 있으면 된다 — 최근 것부터 오므로
 * 한 트레이 9칸이 넉넉히 들어갈 만큼만 받는다.
 */
const DISPENSE_PASS_LIMIT = 50

/**
 * 시료 흐름 한 벌을 만든다 — **컨테이너가 부르는 유일한 자리.**
 *
 * 🔴 **작업을 고르는 규칙을 복제하지 않는다.** 상시 작업 줄(`BatchStripContainer`)·모니터링
 *    따라가기(`useRunningDeviceKey`)와 **같은 함수**(`pickStripBatch`)에 **같은 쿼리 인자**를
 *    쓴다. 세 곳이 같은 작업을 말해야 하고, 인자가 같아야 요청이 겹치지 않는다.
 *
 * 🔴 **구독이 이 화면을 «움직이는» 길.** 새 채널을 열지 않는다 — `sample_lifecycle` 프레임이
 *    오면 다리(`stream/bridge.ts`)가 `keys.samples.all()` 을 무효화하고 `GET /samples` 가
 *    다시 온다. 실물 프레임이 배치당 4건(`centrifuged`→`assembled`→`staining`→`completed`)
 *    이므로 **단계가 넘어갈 때마다 카드가 같이 넘어간다.**
 *    ⚠ 그 프레임에는 `sample_id` 가 없다(배치 단위 · eric 결정 2026-09-14) — 그래서 보관소를
 *      따로 두지 않고 **서버에 다시 묻는 편**이 맞다. 서버가 완전한 집합을 준다(SSOT).
 */
export interface SampleFlowOptions {
  /** `GET /samples` 응답. 못 봤으면 `undefined` */
  samples: readonly Sample[] | undefined
  /** 못 본 사유(viewer 403 · 조회 중). 🔴 있으면 카드 대신 이 문구가 선다 */
  notice?: string | undefined
  /** 구독이 말한 지금 도는 모듈. `undefined` 면 「진행 중」 칸이 한 칸도 안 선다 */
  runningDevice?: MonitoringDeviceKey | undefined
  /**
   * 🔴 **단계 시각 조회를 끈다** — 데이터 소스가 `demo` 일 때. fixture 화면이 실물 서버를
   *    때리면 목의 뜻이 사라지고, demo 시료 ID 로는 `404` 만 돌아온다.
   * ⚠ 끄면 시각 줄이 전부 `—` 다. 그것이 사실이다(그 시료의 보고서를 본 적이 없다).
   */
  skipTimes?: boolean | undefined
  onOpenSample?: ((sampleId: string) => void) | undefined
  /** 오른쪽 판에 열린 시료 — 그대로 화면에 싣는다 */
  selectedSampleId?: string | undefined
  /**
   * 서버 트레이 뷰(`GET /monitoring/trays` · 그림과 같은 응답). 🔴 **배치 추적을 못 받았을 때만** 챔버 «자리»로 진행을 칠한다
   * (`trayChamberProgress`). 추적이 그 웰을 말하면 쓰지 않는다. demo 는 안 넘긴다.
   */
  trayView?: MonitoringTraysView | undefined
}

export function useSampleFlow(options: SampleFlowOptions): SampleFlowView {
  const {
    samples,
    notice,
    runningDevice,
    skipTimes = false,
    onOpenSample,
    selectedSampleId,
    trayView,
  } = options
  /*
   * 🔴 **작업 줄·따라가기와 «같은 문»으로 받는다**(`useActiveBatchSource`). 질의 인자가 같아야
   *    요청이 겹치지 않고, **demo 에서도 같은 작업이 선다** — 여기서만 `skip` 하면 목 화면에서
   *    시료 흐름만 통째로 비어 「모듈은 실행 중인데 작업은 없다」가 된다(2026-09-21 사고).
   */
  const { batches, recipes } = useActiveBatchSource()
  // 🔴 「끊김」 띠는 끊겼을 때만 — 새로고침 직후 붙는 중에는 안 그린다(`isFlowStale` 주석)
  const eventsStale = isFlowStale(useStreamState('events'))

  // 🔴 작업 줄과 같은 «고른 작업»을 읽는다 — 안 읽으면 줄과 시료 판이 다른 작업을 말한다
  const batch = useStripBatch(batches)

  /*
   * 🔴 **묶음 구성은 «레시피»가 준다**(환자별 챔버 그림 · eric 지시 2026-09-21).
   *    새 요청을 보내지 않는다 — 목록이 `payload` 전문을 통째로 실어 오고(`useRecipes` 머리말)
   *    작업 줄·카드가 이미 **같은 인자**로 그 목록을 받아 두었다.
   * 🔴 **읽는 함수는 한 벌이다**(`sampleGroupFacts`). 레시피 화면의 「트레이 · 경로」 카드와
   *    같은 함수라 두 화면이 같은 수를 말한다 — 여기서 `sample_groups` 를 다시 파싱하지 마라.
   */
  const recipe = recipes.find((item) => item.recipe_id === batch?.recipe_id)
  const groups = recipe === undefined ? [] : sampleGroupFacts(recipe.payload).groups

  /*
   * 🔴 **「지금 도는 장비」의 단일 값.** 끊겼으면 `undefined` 다 — 마지막으로 안 모듈을 현재처럼
   *    그리지 않는다. 카드의 「진행 중」 칸 · 단계 줄 · 임시 목의 마개가 **같은 값**을 쓴다.
   *    갈라 쓰면 카드는 원심이 돈다고 하고 목은 염색까지 지났다고 하는 일이 생긴다.
   */
  const liveDevice = eventsStale ? undefined : runningDevice

  /*
   * 🔴 **그 한 작업의 시료만 그린다.** 전체 목록을 그리면 어제 끝난 작업의 시료 27건이
   *    「완료」 줄로 섞여 들어와 지금 무슨 일이 일어나는지가 묻힌다.
   *
   * 🔴 작업이 없으면 시료 카드는 없고 점선 자리 카드가 선다(eric 2026-09-22) — **마지막 작업으로 채우지 않는다.**
   *    종착한 작업을 붙들던 길(팀장 결정)은 같은 날 eric 이 뒤집어 걷었다(`activeBatchSource.ts` 머리말).
   */
  const ofBatch = samplesOfBatch(samples ?? [], batch?.batch_id)
  const flowing = flowSamples(ofBatch)

  /*
   * ── 보고서 ──────────────────────────────────────────────────────────────
   * 🔴 **시각과 판정이 «같은 응답»에서 온다.** 두 축 다 `GET /reports/{id}.events[]` 이고
   *    요청은 시료당 한 번뿐이다.
   * 🔴 **도는 작업에 묶인 시료만 부른다.** 목록 전체(실측 111건)를 넣으면 보고서 요청이
   *    111개 나간다. `flowing` 은 이미 그 작업으로 좁혀진 목록이다.
   * 🔴 **폴링이 아니다**(DP-01). `sample_lifecycle` 프레임이 `keys.samples` 를 무효화하면
   *    `GET /samples` 가 다시 오고, 그 응답이 바뀌어야 여기 `sampleIds` 가 바뀐다. 보고서는
   *    그때만 다시 온다 — 캐시 키가 `keys.reports.detail(id)` 라 시료 집합이 같으면 요청 0건이다.
   * ⚠ **viewer 는 403 이다**(`app/views/reports.py` `REPORT_ROLES = {operator, admin}` · 실측).
   *   그 실패는 **시각 칸만** 접는다 — 카드를 죽이지 않는다. 실제로 viewer 는 `GET /samples`
   *   에서 먼저 403 이라 카드 자리에 그쪽 사유가 서고, 이 사유는 그때 그려지지 않는다
   *   (`SampleBundles` 가 `notice` 를 우선한다) — 사유 두 개를 겹쳐 적지 않기 위해서다.
   */
  const sampleIds = flowing.map((sample) => sample.sample_id)
  const reports = useSampleReports({ sampleIds, skip: skipTimes })
  /*
   * 🔴 **이번 회차가 시작된 뒤의 사건만 쓴다**(`stageTimes.ts` 머리말 — 보고서 `events` 가
   *    배치로 걸러지지 않아 어제 회차의 시각이 섞여 온다). 작업이 아직 안 시작됐으면
   *    `started_at` 이 없고, 그때는 시각도 판정도 **한 칸도 안 선다.**
   */
  const since = batch?.started_at ?? undefined
  const times = stageTimeLookup(reports.byId, since)

  const rows = flowRows({
    samples: flowing,
    /* 🔴 끊겼으면 「지금」을 모른다 — 마지막으로 안 모듈을 현재처럼 그리지 않는다 */
    ...(liveDevice === undefined ? {} : { runningDevice: liveDevice }),
    /*
     * 🔴 **판정은 «서버가 준 `error_code`» 하나다**(2026-09-22 · `stageVerdicts.ts` 머리말).
     *    2026-09-21~22 에는 이 자리에 임시 목(`temporaryStageVerdicts.ts`)이 서서 시료 ID
     *    해시로 실패를 지어냈고, 실물 회차에서 **서버가 전건 `null` 이라고 말하는 시료 3건에
     *    빨간 테를 그렸다.** 그 파일은 걷었다 — 되살리지 마라.
     * 🔴 **지금은 실패가 0건인 것이 정답이다.** 서버의 insert 가 그 칸을 안 써서다(BR-49).
     *    값이 오기 시작하면 **코드 수정 없이** 그대로 실패로 선다.
     */
    verdicts: stageVerdictLookup(reports.byId, since),
  })

  /*
   * 🔴 **카드는 격자가 쓰던 «그 계산»을 그대로 쓴다.** 격자 화면은 걷었지만 계산
   *    (`flowRows`)은 한 곳에 남는다 — 카드의 단계 줄과 챔버 칩이 같은 줄에서 나온다.
   * 🔴 **임시 목이 끼는 자리는 이제 하나뿐이다**(`temporarySampleGroups.ts` · BR-31). 이 줄을
   *    빼면 묶음 카드의 시료 이름 자리가 「배정 정보 없음」이 되고 시료는 **챔버 없는 카드**로
   *    따로 선다 — 그것이 서버가 주는 전부다.
   */

  /*
   * 🔴 **배정은 서버 값이 먼저다**(`serverSampleGroups` · `sample_ref` · `assigned_well`). 서버가 배정을 안 준
   *    작업(배정 없이 만든 배치)에서만 임시 규칙(`temporarySampleGroups` — 표지된 목)으로 떨어진다.
   */
  /*
   * 🔴 **viewer — 시료 목록을 못 받아도 카드는 선다**(eric 2026-09-28 「일단 권한 신경쓰지 말고 보이게 해줘」).
   *    `GET /samples` 는 viewer 403 이지만 `GET /batches` 의 `sample_assignments`(웰 ↔ 시료 번호)는 viewer 도 받는다.
   *    그때는 그 배정이 카드의 주 자료다 — 시료 단계·대조 여부 같은 부가 값만 빠진다(부가 조회 규율).
   *    대조 여부는 레시피 묶음의 `role` 이 말한다(카드 머리 배지 — 이름으로 짐작하지 않는다).
   */
  const assignments = batch?.sample_assignments ?? []
  const assignmentOnly = samples === undefined && assignments.length > 0
  const byAssignment: GroupAssignment = assignmentSampleGroups(assignments)
  const assign = assignmentOnly
    ? byAssignment
    : (serverSampleGroups(ofBatch) ?? temporarySampleGroups)

  const stages = batchFlowCells({
    samples: flowing,
    ...(liveDevice === undefined ? {} : { runningDevice: liveDevice }),
  })
  const phase = chamberPhase(stages)
  const tracking = useBatchTracking({
    batchId: batch?.batch_id,
    skip: skipTimes || eventsStale || batch === undefined,
  })
  const trackingChambers = new Map(
    (!skipTimes && !eventsStale && tracking.isSuccess && tracking.data.batch_id === batch?.batch_id
      ? tracking.data.chambers
      : []
    ).map((chamber) => [chamber.well, chamber] as const),
  )
  /*
   * ── 추적을 못 받았을 때의 갈래(`chamberProgress` 머리말) ─────────────────────────────────────────────
   * 🔴 **서버 추적(`ChamberTrack.active` · `phase_mark` · `recipe_step`)이 먼저다.** 아래 셋은 추적 조회가 «실패»했거나 응답에
   *    챔버가 없을 때만 쓴다 — 조회 중에는 부르지 않는다(받으면 버릴 요청이라서).
   *    · 염색 분주 기록(웰 근거) — 폴링 아님(DP-01 · 분주 프레임이 무효화) · viewer 403 이면 모듈 근거로 떨어진다
   *    · 트레이 뷰(자리 근거) — 컨테이너가 그림용으로 이미 받은 응답이다(새 요청 0건)
   *    · 실행 끝(`settled`) — 마지막 진행 프레임이 종착이다(구독 캐시 · 요청 0건)
   * ⚠ 6축 이송(`transfer`)은 되살리지 않는다 — 그 근거가 같은 추적 응답(트레이 `where`)이라 추적을 못 받으면 그것도 모른다.
   */
  const trackingMissing =
    !skipTimes &&
    !eventsStale &&
    (tracking.isError || (tracking.isSuccess && trackingChambers.size === 0))
  const stainingNow = phase?.stage === 'staining' && !phase.done
  const dispense = useDispenseRecords({
    ...(batch === undefined ? {} : { batch_id: batch.batch_id }),
    limit: DISPENSE_PASS_LIMIT,
    skip: !trackingMissing || batch === undefined || !stainingNow,
  })
  const pass =
    trackingMissing && stainingNow && dispense.isSuccess
      ? currentDispensePass(dispense.data.items)
      : undefined
  const lastProgress = useBatchProgress(batch?.batch_id)
  const settled = trackingMissing && isTerminalExecutionEvent(lastProgress?.eventType)
  const trayChambers = new Map(
    (trackingMissing ? (trayView?.chambers ?? []) : []).map(
      (chamber) => [chamber.well, chamber] as const,
    ),
  )
  /*
   * 🔴 **웰 → 그 챔버의 시료 — 카드 = 시료 하나**(eric 2026-10-11 「TEST-…-02 이거 하나하나 시료 아니야?」 → 「챔버 하나에 시료 하나면
   *    하나씩 되어야지」). 종전에는 레시피 묶음(A1+B1) 카드가 대표 시료 하나만 그려 같은 묶음의 다른 시료(A1 의 -01)가 화면에 없었다.
   *    서버 배정이 먼저(`Batch.sample_assignments` — viewer 도 받는다), 없으면 시료의 `assigned_well`. 둘 다 없으면 넘기지 않는다(종전 묶음 카드).
   */
  const wellOwner = new Map<string, string>()
  for (const sample of ofBatch)
    if (sample.assigned_well != null) wellOwner.set(sample.assigned_well, sample.sample_id)
  for (const a of assignments) wellOwner.set(a.wellName, a.sample_id)
  const chambers = patientChambers({
    groups,
    rows,
    assign,
    ...(wellOwner.size === 0 ? {} : { wellOwners: wellOwner }),
    grouped: groupedSampleIds(ofBatch, groups),
    times,
    progressOf: (well) => {
      const track = trackingChambers.get(well)
      const own =
        track !== undefined && track.sample_id === wellOwner.get(well)
          ? trackingChamberProgress(track)
          : undefined
      if (own !== undefined) return own
      return chamberProgress({ well, phase, pass, settled, chamber: trayChambers.get(well) })
    },
    /* 시료 줄이 없는 viewer 카드는 배치 단계 줄의 대표 칸을 입는다(시료 단계를 지어내지 않는다) */
    ...(assignmentOnly
      ? {
          assignedFallback: (() => {
            const cell = speakingCell({ cells: stages } as Parameters<typeof speakingCell>[0])
            return { state: cell.state, stage: cell.stage }
          })(),
        }
      : {}),
  })

  return {
    /*
     * 🔴 **데크 랙 그림의 정의 출처**(eric 2026-09-27 「오픈트론의 랩웨어 … 거기서 가지고 와서 그려」) —
     *    묶음 구성과 **같은 레시피**다. 작업이 없으면 안 싣는다(그림이 번들 사본으로 선다).
     */
    ...(() => {
      /* 옛 형식은 `payload.labware` · `payload.reagents` 그대로, 새 형식은 `loadLabware` · `loadLiquid` 에서(`recipeV2View`) */
      const deckLabware = labwareOf(recipe?.payload)
      return deckLabware === undefined ? {} : { deckLabware }
    })(),
    /* 같은 레시피의 시약 — 소모품 요약의 «시약을 둔 웰» · 서랍 튜브 랙의 시약 테. 작업이 없으면 안 싣는다 */
    ...(() => {
      const deckReagents = reagentsOf(recipe?.payload)
      return deckReagents === undefined ? {} : { deckReagents }
    })(),
    /* 🔴 화면이 그리는 것은 **카드 한 벌**이다 — 격자를 걷으면서 `rows` 는 내려가지 않는다 */
    bundles: chambers.bundles,
    /*
     * 🔴 **단계 줄은 «배치 단위»로 한 벌이다**(eric 2026-09-23 「현재 진행 상태를 한쪽으로
     *    모는 건 어때?」). 시료 카드마다 있던 5단 스테퍼를 걷고 구역 머리에 이것 하나를 세운다.
     * 🔴 **시료들의 다수결이 아니다** — 구독 `sample_lifecycle` 이 `batch_id` 만 싣고
     *    `sample_id` 를 안 싣는다(실물 18건 전수 2026-09-23). 표지는 배치에 찍힌다.
     */
    batchStages: stages,
    /*
     * ⚠ **단계별 시각은 안 내려간다**(eric 2026-09-23 「이거 시간도 없애줘 … 시간도 위에
     *   있잖아」). 칸에 든 것은 이름 한 토큰뿐이라 `batchStageTimes` 를 같이 걷었다.
     */
    tallies: stageTallies(rows, liveDevice),
    /*
     * 🔴 **보고서를 «못 본» 사유는 그 구역에만 적는다.** 카드를 죽이지 않는다 —
     *    주 자료는 `GET /samples` 이고 보고서는 **덧붙는 자료**다(부가 조회 규율).
     * ⚠ 단계 줄의 시각은 걷었다(2026-09-23) — 보고서가 남는 까닭은 **판정**
     *   (`stageVerdictLookup`)과 카드의 「배치와 다름」 줄에 붙는 마지막 표지 시각이다.
     * ⚠ 조회 중은 사유가 아니다 — 잠깐 비었다가 차는 것이 맞다.
     */
    ...(reports.error === undefined
      ? {}
      : { timesNotice: `${t('monitoring.flow.timesUnavailable')} ${errorMessage(reports.error)}` }),
    /*
     * 🔴 배정으로 카드를 세웠으면 시료를 못 본 사유는 카드를 덮지 않고 **구역 한 줄**로 내려간다
     *    (시료 정보 · 진행 · 분주 기록이 빠진 까닭).
     */
    ...(notice === undefined ? {} : assignmentOnly ? { timesNotice: notice } : { notice }),
    stale: eventsStale,
    ...(onOpenSample === undefined ? {} : { onOpenSample }),
    ...(selectedSampleId === undefined ? {} : { selectedSampleId }),
  }
}
